import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Check, ExternalLink, ImageOff, Upload } from "lucide-react";
import { useDataProvider, useGetList, useNotify } from "ra-core";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { MARKETING_PATHS } from "./paths";
import {
  MAIL_CLASSES,
  PCM_SIZES,
  checkCreative,
  nextTouch,
  saveCreative,
  touchRemovalVerdict,
} from "./materialsRules";
import type { CreativeDraft, TouchUsage } from "./materialsRules";

/**
 * Campaign materials — which design each touch uses, and whether it can ship.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * PCM HOLDS THE DESIGN. THIS SCREEN HOLDS THE FACTS ABOUT IT.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * It used to hold the copy as well -- a headline box, a body box, and two
 * artwork uploads -- because when 019 wrote it there was nowhere else for any
 * of that to live. There is now: the artwork, the layout and the words are in
 * PCM, revised there, proofed there. Keeping a second copy here meant two
 * places to change and no way to tell which one the printer used.
 *
 * WHAT DOES NOT MOVE TO PCM, and is why this screen still exists: which
 * design each touch used, what its QR pointed at, what size and class it went
 * as, and who approved it. Every response number depends on those five facts
 * and none of them is recoverable from a design tool six months later.
 *
 * IT IS ALSO THE ONLY WRITER OF pcm_design_id. Migration 047 added the column
 * and creative_for_send.use_pcm_design reads it, so send-drop takes the PCM
 * path the moment it is set -- and until this screen, nothing set it. The
 * whole design linkage was a column set nobody could reach.
 *
 * PLANNING LIVES IN DROPS, NOT HERE. `drop_board` already answers which cell
 * gets which touch and when, and Drops.tsx already renders it. This screen
 * links there rather than growing a second, disagreeing calendar.
 */

/** One row of `public.creatives`, reduced to what this screen reads. */
type Creative = {
  id: number;
  touch: string;
  name: string;
  status: string;
  offer_url: string | null;
  qr_target: string | null;
  notes: string | null;
  artwork_bucket: string;
  artwork_front_path: string | null;
  pcm_design_id: number | null;
  pcm_size_key: string | null;
  mail_class: string | null;
  proof_front_url: string | null;
  proof_back_url: string | null;
  approved_at: string | null;
  thumbnail_path: string | null;
};

/** One row of `public.pcm_designs` — the library PCM holds (086). */
type PcmDesignRow = {
  id: number;
  design_id: number;
  friendly_name: string | null;
  size_key: string | null;
  size_label: string | null;
  approval_datetime: string | null;
  thumbnail_path: string | null;
  synced_at: string;
};

/** One row of `public.creative_readiness` (085). */
type Readiness = {
  id: number;
  touch: string;
  printable: boolean;
  use_pcm_design: boolean;
  has_thumbnail: boolean;
  has_proof: boolean;
};

/**
 * THE BOARD FOLLOWS THE DATA. It used to be Array.from({length: 6}), which
 * put the number six in one line of TSX and nowhere else -- not in a table,
 * not in a constraint. A seventh touch inserted in the database did not
 * appear, and there was no way to add or remove one at all.
 *
 * `creatives_touch_check` allows ^T[0-9]{2}$, so the real ceiling is T99 and
 * the real set is whatever rows exist.
 */
const touchesOf = (creatives: readonly Creative[]) =>
  [...new Set(creatives.map((c) => c.touch))].sort();

const draftOf = (c: Creative | undefined, touch: string): CreativeDraft => ({
  name: c?.name ?? `Touch ${Number(touch.slice(1))}`,
  status: c?.status ?? "draft",
  offerUrl: c?.offer_url ?? "",
  pcmDesignId: c?.pcm_design_id == null ? "" : String(c.pcm_design_id),
  mailClass: c?.mail_class ?? "",
  notes: c?.notes ?? "",
});

/**
 * The stored thumbnail, or an honest gap.
 *
 * A SIGNED URL, NOT A PUBLIC ONE: the bucket is private because unreleased
 * creative should not be readable by anyone who guesses a path. Signing is
 * per-render and short-lived, which is why this is a component with an effect
 * rather than a field on the row.
 */
const Thumb = ({
  bucket,
  path,
  alt,
}: {
  bucket: string;
  path: string | null;
  alt: string;
}) => {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    if (!path) {
      setUrl(null);
      return;
    }
    void (async () => {
      const { getSupabaseClient } = await import(
        "../providers/supabase/supabase"
      );
      const { data } = await getSupabaseClient()
        .storage.from(bucket)
        .createSignedUrl(path, 600);
      if (alive) setUrl(data?.signedUrl ?? null);
    })();
    return () => {
      alive = false;
    };
  }, [bucket, path]);

  if (!path || !url)
    return (
      <div className="flex h-56 w-full items-center justify-center rounded-md border border-dashed text-muted-foreground">
        <ImageOff className="size-5" />
      </div>
    );

  return (
    <img
      src={url}
      alt={alt}
      /*
        object-CONTAIN, NOT COVER. A postcard is 4.25 x 6 and the box is not,
        so `cover` crops it — and what it crops is the edge of a design
        somebody is about to spend money printing. Letterboxing shows the
        whole card; the muted ground makes the empty space read as deliberate
        rather than as a broken image.
      */
      className="h-56 w-full rounded-md border bg-muted/40 object-contain"
    />
  );
};

const CreativeCard = ({
  touch,
  existing,
  readiness,
  library,
  usage,
  onSaved,
}: {
  touch: string;
  existing: Creative | undefined;
  readiness: Readiness | undefined;
  /** The designs PCM holds, to pick from rather than type an id. */
  library: PcmDesignRow[];
  /** What else references this touch, for the removal check. */
  usage: TouchUsage;
  onSaved: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => draftOf(existing, touch));

  useEffect(() => setDraft(draftOf(existing, touch)), [existing, touch]);

  const set = (patch: Partial<CreativeDraft>) =>
    setDraft((d) => ({ ...d, ...patch }));
  const check = checkCreative(draft, existing ?? {});
  const removal = touchRemovalVerdict(touch, usage);
  const linkedDesign = library.find(
    (d) =>
      existing?.pcm_design_id != null && d.design_id === existing.pcm_design_id,
  );

  const run = async (what: string, action: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await action();
      notify(what, { type: "info" });
      onSaved();
    } catch (e) {
      notify(String((e as Error)?.message ?? e), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const uploadThumb = (file: File) =>
    run(`${touch} thumbnail saved`, async () => {
      const { getSupabaseClient } = await import(
        "../providers/supabase/supabase"
      );
      // The touch is in the path so the bucket stays readable by a person, and
      // a timestamp so a revision never overwrites the image that went out.
      const path = `${touch}/thumb-${Date.now()}-${file.name.replace(/[^\w.-]/g, "_")}`;
      const { error } = await getSupabaseClient()
        .storage.from("campaign")
        .upload(path, file, { upsert: false });
      if (error) throw new Error(error.message);

      if (existing)
        await dataProvider.update("creatives", {
          id: existing.id,
          data: { thumbnail_path: path },
          previousData: existing,
        });
      else
        await dataProvider.create("creatives", {
          data: { touch, name: draft.name, thumbnail_path: path },
        });
    });

  const save = () => {
    if (!check.patch) return;
    void run(`${touch} saved`, () =>
      saveCreative(
        dataProvider,
        existing?.id ?? null,
        touch,
        check.patch!,
        existing?.status === "approved",
      ),
    );
  };

  return (
    <li className="space-y-2 rounded-md border p-3">
      <Thumb
        bucket={existing?.artwork_bucket ?? "campaign"}
        path={existing?.thumbnail_path ?? null}
        alt={`${touch} card`}
      />

      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-sm font-medium">{touch}</span>
        {/*
          READY IS THE VIEW'S ANSWER, not one computed here. creative_readiness
          asks the question send-drop asks, and 085 exists because those two
          used to disagree. Recomputing it on this screen would be a third
          opinion.
        */}
        {readiness?.printable ? (
          <Badge variant="default" className="gap-1">
            <Check className="size-3" /> Ready
          </Badge>
        ) : (
          <Badge variant="outline">{existing?.status ?? "not created"}</Badge>
        )}
        {readiness?.use_pcm_design && (
          <Badge variant="outline">PCM design</Badge>
        )}
      </div>

      <p className="text-sm leading-snug">{draft.name}</p>
      {/*
        THE DESIGN'S NAME, WHICH IS NOT THE TOUCH'S. PCM calls design 36229
        "Do you trust your inventory"; this card is called "Touch 1 — The
        category problem". Both are true and they answer different questions,
        so the card shows both rather than one overwriting the other — and
        the design's is read live from the catalog, so it follows a rename in
        PCM without a sync.
      */}
      {linkedDesign && (
        <p className="text-xs text-muted-foreground">
          PCM:{" "}
          {linkedDesign.friendly_name ?? `Design ${linkedDesign.design_id}`}
          {/*
            PCM'S OWN APPROVAL, WHICH IS NOT OURS. It says PCM signed off the
            artwork; `status` says we have decided to spend money mailing it.
            Design 34749 carries one of these and 36229 does not, and neither
            implies the other — showing both is what stops one being read as
            the other.
          */}
          {linkedDesign.approval_datetime
            ? ` · approved at PCM ${linkedDesign.approval_datetime.slice(0, 10)}`
            : " · not approved at PCM"}
        </p>
      )}

      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
        <dt>Design</dt>
        <dd>{existing?.pcm_design_id ?? "— not linked"}</dd>
        <dt>Size</dt>
        <dd>
          {PCM_SIZES.find((s) => s.key === existing?.pcm_size_key)?.label ??
            "—"}
          {existing?.mail_class ? ` · ${existing.mail_class}` : ""}
        </dd>
        <dt>QR</dt>
        <dd className="truncate">{existing?.qr_target ?? "—"}</dd>
      </dl>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          className="h-7"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "Close" : "Edit"}
        </Button>
        {/*
          NOTHING IN THE DATABASE STOPS THIS DELETE. mailings.touch and
          drop_schedule.touch are plain text with no foreign key to creatives,
          so the guard has to be here — otherwise the record of SENDING would
          outlive the record of what was sent.
        */}
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-destructive"
          disabled={busy || !removal.ok}
          title={removal.ok ? undefined : removal.reason}
          onClick={() =>
            existing &&
            run(`${touch} removed.`, () =>
              dataProvider.delete("creatives", {
                id: existing.id,
                previousData: existing,
              }),
            )
          }
        >
          Remove
        </Button>

        {existing?.proof_front_url && (
          <a
            href={existing.proof_front_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs underline underline-offset-2 text-muted-foreground"
          >
            Proof <ExternalLink className="size-3" />
          </a>
        )}
      </div>

      {open && (
        <div className="space-y-3 border-t pt-3">
          <div className="space-y-1">
            {/*
              OURS, AND THE LABEL SAYS SO. PCM has its own name for the design
              and it is shown on the card; this one is what the touch is
              called in the campaign, and it is what drop_board reads back
              months later as creative_name.
            */}
            <Label htmlFor={`${touch}-name`}>Our name for this touch</Label>
            <Input
              id={`${touch}-name`}
              value={draft.name}
              onChange={(e) => set({ name: e.target.value })}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {/*
              A PICKER, NOT A TYPED ID. This was a number entered by hand and
              checked by nothing until a send failed; 086 made it a foreign key
              into rows PCM itself supplied, so the only ids offered are ones
              that exist. An empty library means nobody has synced yet, and the
              option says so rather than presenting an empty box.
            */}
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor={`${touch}-design`}>PCM design</Label>
              <select
                id={`${touch}-design`}
                value={draft.pcmDesignId}
                onChange={(e) => set({ pcmDesignId: e.target.value })}
                className="h-9 w-full rounded-md border bg-transparent px-3 text-sm"
              >
                <option value="">
                  {library.length === 0
                    ? "No designs yet — press Sync designs from PCM"
                    : "None"}
                </option>
                {library.map((d) => (
                  <option key={d.design_id} value={String(d.design_id)}>
                    {d.friendly_name ?? `Design ${d.design_id}`}
                    {d.size_label ? ` · ${d.size_label}` : ""}
                  </option>
                ))}
                {/*
                  A design this card already points at but the library does
                  not list stays selectable, so opening the card cannot
                  silently unlink it. It happens whenever an id predates the
                  catalog — creatives.T01 pointed at 36229 before 086 existed.
                */}
                {draft.pcmDesignId !== "" &&
                  !library.some(
                    (d) => String(d.design_id) === draft.pcmDesignId,
                  ) && (
                    <option value={draft.pcmDesignId}>
                      {draft.pcmDesignId} — not in the synced library
                    </option>
                  )}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${touch}-class`}>Mail class</Label>
              <select
                id={`${touch}-class`}
                value={draft.mailClass}
                onChange={(e) => set({ mailClass: e.target.value })}
                className="h-9 w-full rounded-md border bg-transparent px-3 text-sm"
              >
                <option value="">—</option>
                {MAIL_CLASSES.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/*
            SIZE AND THE QR DESTINATION ARE STATED, NOT TYPED. PCM's own spec
            calls size.key "Internal size key" and the QR's url "URL this QR
            Code points to" — both are properties of the artwork. A box for
            either meant two places held one fact and only one of them was
            printed.
          */}
          <p className="text-xs text-muted-foreground">
            Size and the QR destination come from PCM.{" "}
            {existing?.pcm_size_key
              ? `This design is ${PCM_SIZES.find((x) => x.key === existing.pcm_size_key)?.label ?? existing.pcm_size_key}.`
              : "Sync to find out what this design is."}
          </p>

          {/*
            NEARLY IRRELEVANT, AND WORTH SAYING WHICH PART IS NOT.

            PCM does not state the printed URL: designFields came back empty
            for both real designs, so there is nothing to sync. Its only
            reader is pipeline/send_drop.py, as `qr_url = qr_target or
            offer_url` — a FALLBACK, which now that the QR target is synced
            from PCM can only ever fire for a card with no design linked.

            So it stays, demoted, with the label saying when it matters.
            Deleting it would quietly change what a pre-PCM card sends.
          */}
          <div className="space-y-1">
            <Label htmlFor={`${touch}-offer`}>
              Printed URL — only used if no design is linked
            </Label>
            <Input
              id={`${touch}-offer`}
              value={draft.offerUrl}
              placeholder={
                existing?.qr_target
                  ? `unused — PCM says ${existing.qr_target}`
                  : "https://barrelscout.com/find"
              }
              onChange={(e) => set({ offerUrl: e.target.value })}
            />
          </div>

          {/*
            NOT IRRELEVANT: send-drop refuses a live send unless this says
            approved (index.ts:483). It is OUR gate on spending money, and it
            is not PCM's approvalDateTime — design 34749 carries one of those
            and 36229 does not, and neither says anything about whether we
            have decided to mail it.
          */}
          <div className="space-y-1">
            <Label htmlFor={`${touch}-status`}>
              Status — send-drop refuses a live send unless this is approved
            </Label>
            <select
              id={`${touch}-status`}
              value={draft.status}
              onChange={(e) => set({ status: e.target.value })}
              className="h-9 w-full rounded-md border bg-transparent px-3 text-sm sm:w-48"
            >
              <option value="draft">draft</option>
              <option value="approved">approved</option>
              <option value="retired">retired</option>
            </select>
          </div>

          <div className="space-y-1">
            <Label htmlFor={`${touch}-notes`}>Notes</Label>
            <Textarea
              id={`${touch}-notes`}
              rows={2}
              value={draft.notes}
              onChange={(e) => set({ notes: e.target.value })}
            />
          </div>

          {check.errors.length > 0 && (
            <ul className="space-y-1 text-xs text-destructive">
              {check.errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              className="h-7"
              disabled={busy || !check.changed || check.errors.length > 0}
              onClick={save}
            >
              Save
            </Button>

            <label className="inline-flex cursor-pointer items-center gap-1 text-xs underline underline-offset-2 text-muted-foreground">
              <Upload className="size-3" />
              Upload thumbnail
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void uploadThumb(file);
                }}
              />
            </label>
          </div>

          {existing?.approved_at && (
            <p className="text-xs text-muted-foreground">
              Approved {existing.approved_at.slice(0, 10)}.
            </p>
          )}
        </div>
      )}
    </li>
  );
};

export const Materials = () => {
  const { data: creatives, refetch: refetchCreatives } = useGetList<Creative>(
    "creatives",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "touch", order: "ASC" },
      filter: {},
    },
  );

  const {
    data: designs,
    error: designsError,
    refetch: refetchDesigns,
  } = useGetList<PcmDesignRow>("pcm_designs", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "friendly_name", order: "ASC" },
    filter: {},
  });

  const { data: readiness, refetch: refetchReadiness } = useGetList<Readiness>(
    "creative_readiness",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "touch", order: "ASC" },
      filter: {},
    },
  );

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [syncing, setSyncing] = useState(false);

  const onSaved = () => {
    void refetchCreatives();
    void refetchReadiness();
    void refetchDesigns();
  };

  const library = designs ?? [];

  /**
   * One button, every linked card.
   *
   * A PER-CARD BUTTON MADE SYNCING A CHORE proportional to the number of
   * touches, and the thing a person wants is "make this screen agree with
   * PCM" rather than six separate agreements. It also hid itself until the
   * card was saved, because it keyed off the stored row while the design id
   * was still in the form — the control vanished exactly when you reached
   * for it.
   *
   * ONE CARD'S FAILURE IS NOT THE BATCH'S. The function syncs what it can and
   * reports the rest, because "nothing worked" and "one of six needs
   * attention" are different reports and only the second is actionable.
   */
  const syncAll = async () => {
    setSyncing(true);
    try {
      const { getSupabaseClient } = await import(
        "../providers/supabase/supabase"
      );
      const { data, error } = await getSupabaseClient().functions.invoke<{
        designs?: number;
        refreshed?: number;
        of?: number;
        notes?: string[];
        results?: Array<{
          touch: string;
          ok: boolean;
          error?: string;
          notes?: string[];
        }>;
      }>("pcm-designs", { body: {} });

      if (error) throw new Error(error.message);

      notify(
        `${data?.designs ?? 0} design${data?.designs === 1 ? "" : "s"} in PCM; ` +
          `${data?.refreshed ?? 0} of ${data?.of ?? 0} cards refreshed.`,
        { type: "info" },
      );
      for (const n of data?.notes ?? []) notify(n, { type: "warning" });

      // Every card that could not be finished says which one and why. A
      // silent partial success is the failure this whole screen is about.
      for (const r of data?.results ?? []) {
        if (!r.ok) notify(`${r.touch}: ${r.error}`, { type: "error" });
        else
          for (const n of r.notes ?? [])
            notify(`${r.touch}: ${n}`, { type: "warning" });
      }

      onSaved();
    } catch (e) {
      notify(String((e as Error)?.message ?? e), { type: "error" });
    } finally {
      setSyncing(false);
    }
  };

  const cards = creatives ?? [];
  const touches = touchesOf(cards);
  const byTouch = new Map(cards.map((c) => [c.touch, c]));
  const readyByTouch = new Map((readiness ?? []).map((r) => [r.touch, r]));
  const ready = (readiness ?? []).filter((r) => r.printable).length;

  /*
    WHAT A TOUCH IS USED BY, for the removal check. `mailings.touch` and
    `drop_schedule.touch` are plain text with NO foreign key to `creatives`,
    so nothing in the database would stop a delete -- and the record of
    sending would outlive the record of what was sent.
  */
  const { data: mailed } = useGetList<{ id: number; touch: string }>(
    "mailings",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "id", order: "ASC" },
      filter: {},
    },
  );
  const { data: scheduled } = useGetList<{ id: number; touch: string }>(
    "drop_schedule",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "id", order: "ASC" },
      filter: {},
    },
  );

  const usageOf = (touch: string) => ({
    mailings: (mailed ?? []).filter((m) => m.touch === touch).length,
    scheduled: (scheduled ?? []).filter((d) => d.touch === touch).length,
  });

  const addTouch = async () => {
    const next = nextTouch(touches);
    if (!next)
      return notify("There is no free touch number left.", { type: "warning" });
    setSyncing(true);
    try {
      await dataProvider.create("creatives", {
        data: { touch: next, name: `Touch ${Number(next.slice(1))}` },
      });
      notify(`${next} added.`, { type: "info" });
      onSaved();
    } catch (e) {
      notify(String((e as Error)?.message ?? e), { type: "error" });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="p-4 pb-8 max-w-4xl">
      <h1 className="text-lg font-semibold">Materials</h1>

      <p className="mt-1 mb-4 text-sm text-muted-foreground">
        One card per touch. The artwork and the words live in PCM; what is kept
        here is which design each touch used, where its QR pointed, and whether
        it can be printed — the facts every response number depends on, and the
        ones a design tool cannot answer six months later.
      </p>

      {/*
        WHAT "READY" MEANS, ON THE SCREEN THAT USES THE WORD. It is a rule two
        different things used to answer differently, so stating it here is not
        decoration.
      */}
      <p className="rounded-md border border-amber-500/50 bg-amber-500/5 p-3 text-sm">
        <strong>Ready</strong> means approved <em>and</em> there is something to
        print — either a PCM design id or, for anything made before PCM, both
        uploaded sides. That is the same question <code>send-drop</code> asks.
        Until migration 085 this board demanded uploaded artwork and reported a
        finished PCM design as not printable, so the board and the sender
        disagreed about the campaign.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3 border-y py-2 text-sm">
        <span>
          <strong>{ready}</strong> of {touches.length} ready to print
        </span>

        {/*
          AN ERROR MUST NOT LOOK LIKE AN EMPTY LIBRARY. A refused read and a
          library nobody has synced yet both render as zero designs, and only
          one of them is fixed by pressing Sync.
        */}
        {designsError ? (
          <span className="text-destructive">
            Could not read the PCM library, so this is not a report that it is
            empty — it is a report that we do not know.
          </span>
        ) : (
          <span className="text-muted-foreground">
            {library.length} design{library.length === 1 ? "" : "s"} in the PCM
            library
          </span>
        )}

        {/*
          NEVER DISABLED. The point of pulling the library is to find designs
          nobody has picked yet, so gating this on what is already linked
          would make the empty case — the one that matters — the one case it
          refuses to help with.
        */}
        <Button
          size="sm"
          variant="outline"
          className="h-7"
          disabled={syncing}
          onClick={() => void syncAll()}
        >
          {syncing ? "Syncing…" : "Sync designs from PCM"}
        </Button>
        {/*
          PLANNING IS NOT HERE. drop_board already answers which cell gets
          which touch and when. A second calendar on this screen would be a
          second answer.
        */}
        <Link
          to={MARKETING_PATHS.drops}
          className="ml-auto text-sm underline underline-offset-2"
        >
          Plan the drops →
        </Link>
      </div>

      <ul className="mt-4 grid gap-3 sm:grid-cols-2">
        {touches.map((touch) => (
          <CreativeCard
            key={touch}
            touch={touch}
            existing={byTouch.get(touch)}
            readiness={readyByTouch.get(touch)}
            library={library}
            usage={usageOf(touch)}
            onSaved={onSaved}
          />
        ))}
      </ul>

      <div className="mt-3">
        <Button
          size="sm"
          variant="outline"
          className="h-7"
          disabled={syncing}
          onClick={() => void addTouch()}
        >
          + Touch
        </Button>
      </div>
    </div>
  );
};
